import { IconChevronDown, IconSettings, IconSparkles, IconRobot } from '@tabler/icons-react';
import { Avatar, Box, Center, Group, HoverCard, Indicator, Menu, SimpleGrid, Text, Tooltip, ActionIcon, UnstyledButton } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../images/Logo_Black_Smooth.png';
import classes from './Header.module.css';

export function Header({ user, isAuthenticated, userRoles, hasNotStarted, hasPendingReviews, userIsRiskManager, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  const agentLinks = [
    { title: 'AI Agents', description: 'Agent configuration & management', path: '/agents', icon: IconRobot },
    { title: 'Prompt Editor', description: 'Customize AI prompts & models', path: '/prompts', icon: IconSparkles },
  ].map((item) => (
    <UnstyledButton className={classes.subLink} key={item.title} onClick={() => navigate(item.path)}>
      <Group gap="sm" wrap="nowrap">
        <item.icon size={18} stroke={1.5} color="var(--mantine-color-dimmed)" />
        <div>
          <Text size="sm" fw={500}>{item.title}</Text>
          <Text size="xs" c="dimmed">{item.description}</Text>
        </div>
      </Group>
    </UnstyledButton>
  ));

  const isAgentsActive =
    location.pathname.startsWith('/agents') ||
    location.pathname.startsWith('/prompts');

  const isKnowledgeBaseActive =
    location.pathname.startsWith('/knowledge-base') ||
    location.pathname.startsWith('/model-inventory') ||
    location.pathname.startsWith('/risk-catalogue') ||
    location.pathname.startsWith('/universal-uploader') ||
    location.pathname.startsWith('/file-upload') ||
    location.pathname.startsWith('/model-form-config');

  const isSkillsActive =
    location.pathname.startsWith('/skills') ||
    location.pathname.startsWith('/template-manager') ||
    location.pathname.startsWith('/template-editor');

  return (
    <header className={classes.header}>
      <div className={classes.content}>
        {/* Left edge: logo */}
        <button className={classes.edgeButton} aria-label="Home" onClick={() => navigate('/')}>
          <img src={Logo} alt="logo" className={classes.logo} />
        </button>

        {/* Middle: nav */}
        <Group h="100%" gap={0}>
          <button className={classes.link} data-active={location.pathname === '/' || undefined} onClick={() => navigate('/')}>
            Risk Landscape
          </button>

          <Indicator
            disabled={!hasNotStarted && !hasPendingReviews}
            size={8}
            position="top-end"
            offset={5}
            inline
            processing
            color={hasPendingReviews ? "orange" : "red"}
          >
            <button className={classes.link} data-active={location.pathname === '/risk-assessments' || undefined} onClick={() => navigate('/risk-assessments')}>
              Risk Assessments
            </button>
          </Indicator>

          <button className={classes.link} data-active={location.pathname === '/risk-reports' || undefined} onClick={() => navigate('/risk-reports')}>
            Risk Reports
          </button>

          <button className={classes.link} data-active={isKnowledgeBaseActive || undefined} onClick={() => navigate('/knowledge-base')}>
            Knowledge Base
          </button>

          {userIsRiskManager && (
            <button className={classes.link} data-active={isSkillsActive || undefined} onClick={() => navigate('/skills')}>
              Skills
            </button>
          )}

          {userIsRiskManager && (
            <HoverCard width={280} position="bottom" radius="md" shadow="md" withinPortal>
              <HoverCard.Target>
                <button className={classes.link} data-active={isAgentsActive || undefined}>
                  <Center inline>
                    <IconRobot size={16} style={{ marginRight: 4 }} />
                    <Box component="span" mr={5}>AI Agents</Box>
                    <IconChevronDown size={16} />
                  </Center>
                </button>
              </HoverCard.Target>
              <HoverCard.Dropdown style={{ overflow: 'hidden' }}>
                <SimpleGrid cols={1} spacing={0}>{agentLinks}</SimpleGrid>
              </HoverCard.Dropdown>
            </HoverCard>
          )}
        </Group>

        {/* Right edge: avatar menu */}
        {isAuthenticated && user && (
          <Menu>
            <Menu.Target>
              <Tooltip label={`Role: ${userRoles.join(', ') || 'User'}`}>
                <ActionIcon variant="transparent" radius="xl" size={44} className={classes.edgeIcon}>
                  <Avatar src={user.picture} alt={user.name || 'User'} radius="xl" size={44} />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item disabled>Hello, {user?.name || 'User'}</Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconSettings size={16} />} disabled>
                Settings
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item onClick={onLogout}>Logout</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </div>
    </header>
  );
}
